import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { withStyles, Button, CircularProgress, Paper, Typography } from '@material-ui/core';
import FolderOpen from '@material-ui/icons/FolderOpen';
import ArrowForward from '@material-ui/icons/ArrowForward';
import EmptyState from 'components/shared/EmptyState';
import PATHS from 'constants/paths';
import useFileLightbox from '../hooks/useFileLightbox';
import { DEFAULT_MAX_FILES_PER_SLOT } from '../utils';
import FileRow from './FileRow';
import MultiUploadDialog from './MultiUploadDialog';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  cottageHeader: {
    padding: theme.spacing(1.5, 0),
    marginBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  topSection: {
    marginBottom: theme.spacing(3),
  },
  topDescription: {
    marginBottom: theme.spacing(2),
  },
  uploadButton: {
    marginBottom: theme.spacing(1),
  },
  maxFilesWarning: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  allowedTypes: {
    color: theme.palette.grey[500],
    fontSize: '0.75rem',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  fileCard: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
  },
  bottomLink: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(3),
    gap: theme.spacing(0.5),
  },
});

const CottageFilesList = ({
  classes,
  config,
  files,
  cottage,
  cottageId,
  cottageName,
  allCottages,
  counts,
  isLoading,
  isUploading,
  uploadProgress,
  uploadFileIndex,
  uploadFileCount,
  isUpdating,
  onUpload,
  onView,
  onDownload,
  onRename,
  onReassign,
  onDelete,
}) => {
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const uploadDialogRef = React.useRef(null);
  const handleUploadClose = React.useCallback(() => setUploadOpen(false), []);
  const handleUploadOpen = React.useCallback(() => {
    setUploadOpen(true);
    uploadDialogRef.current?.openPicker();
  }, []);
  const { handleView, lightboxElement } = useFileLightbox({
    files,
    onView,
    onDownload,
    subtitle: cottageName,
  });

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  const cottageFiles = files || [];
  const hasFiles = cottageFiles.length > 0;
  const maxPerSlot = config.max_files_per_slot ?? DEFAULT_MAX_FILES_PER_SLOT;
  const uploadCottages = cottage ? [cottage] : [];
  const cottageCount = (counts.listings && counts.listings[cottageId]) || 0;
  const atLimit = cottageCount >= maxPerSlot;

  return (
    <div className={classes.root}>
      <Typography variant="body1" className={classes.cottageHeader}>
        #{cottageId} &nbsp; {cottageName}
      </Typography>

      <div className={classes.topSection}>
        <Typography variant="body1" className={classes.topDescription}>
          <FormattedMessage
            id="hostFiles.cottageTopDescription"
            defaultMessage="Save files linked to this listing that you can easily send to guests in conversations -- e.g. welcome letters, directions or house rules."
          />
        </Typography>
        <Button
          variant="contained"
          color="primary"
          className={classes.uploadButton}
          onClick={handleUploadOpen}
          disabled={atLimit}
        >
          <FormattedMessage id="hostFiles.upload" defaultMessage="Upload file" />
        </Button>
        {atLimit && (
          <Typography className={classes.maxFilesWarning}>
            <FormattedMessage
              id="hostFiles.cottageMaxFiles"
              defaultMessage="Max files ({max}) for this listing reached. Remove a file to upload a new one."
              values={{ max: maxPerSlot }}
            />
          </Typography>
        )}
        <Typography className={classes.allowedTypes}>
          <FormattedMessage id="hostFiles.allowedTypes" defaultMessage="Allowed file types" />
        </Typography>
        <Typography className={classes.allowedTypes}>
          <FormattedMessage
            id="hostFiles.allowedTypesList"
            defaultMessage="PDF, TXT, JPG, PNG, WEBP, HEIC"
          />
        </Typography>
      </div>

      {!hasFiles ? (
        <EmptyState
          icon={FolderOpen}
          title={<FormattedMessage id="hostFiles.noFiles" defaultMessage="No files yet" />}
          description={
            <FormattedMessage
              id="hostFiles.cottageNoFilesDescription"
              defaultMessage="There are no files for this listing yet."
            />
          }
        />
      ) : (
        cottageFiles.map((file) => (
          <Paper key={file.id} variant="outlined" className={classes.fileCard}>
            <FileRow
              file={file}
              cottageId={cottageId}
              cottages={allCottages}
              counts={counts}
              maxCount={maxPerSlot}
              isUpdating={isUpdating}
              onView={handleView}
              onDownload={onDownload}
              onRename={onRename}
              onReassign={onReassign}
              onDelete={onDelete}
            />
          </Paper>
        ))
      )}

      <Button
        component={Link}
        to={PATHS.hostFiles.root}
        color="primary"
        className={classes.bottomLink}
      >
        <FormattedMessage id="hostFiles.viewAllFiles" defaultMessage="View all files in My files" />
        <ArrowForward fontSize="small" />
      </Button>

      <MultiUploadDialog
        ref={uploadDialogRef}
        open={uploadOpen}
        config={config}
        cottages={uploadCottages}
        counts={counts}
        maxCount={maxPerSlot}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        uploadFileIndex={uploadFileIndex}
        uploadFileCount={uploadFileCount}
        onUpload={onUpload}
        onClose={handleUploadClose}
      />

      {lightboxElement}
    </div>
  );
};

CottageFilesList.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  config: PropTypes.shape({
    allowed_mime_types: PropTypes.arrayOf(PropTypes.string),
    max_file_size_bytes: PropTypes.number,
    max_files_per_slot: PropTypes.number,
  }),
  files: PropTypes.arrayOf(PropTypes.shape({})),
  cottage: PropTypes.shape({
    id: PropTypes.number,
    title_sv: PropTypes.string,
  }),
  cottageId: PropTypes.number.isRequired,
  cottageName: PropTypes.string,
  allCottages: PropTypes.arrayOf(PropTypes.shape({})),
  counts: PropTypes.shape({
    listings: PropTypes.shape({}),
  }),
  isLoading: PropTypes.bool,
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  uploadFileIndex: PropTypes.number,
  uploadFileCount: PropTypes.number,
  isUpdating: PropTypes.bool,
  onUpload: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onReassign: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default withStyles(styles)(CottageFilesList);
